import React from "react";
 
interface ButtonProps {
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
  color?: string;
  fullWidth?: boolean; // New prop for controlling width
}
export default function Button({
  onClick,
  type = "button",
  disabled = false,
  loading = false,
  children,
  className,
  color,
  fullWidth = false, // Default to content-based width
  ...rest
}: ButtonProps) {
  const isButtonDisabled = disabled || loading;

  /*
   * CHANGE: the default used to be `color = "#1d4ed8"`, interpolated into
   * `bg-${color}-600` — which builds the class name `bg-#1d4ed8-600`. That is
   * not a class Tailwind can generate: the scanner only sees literal strings
   * in the source, and a hex is not a palette step anyway. Confirmed absent
   * from the built stylesheet, so by default this Button rendered with NO
   * background and white text — invisible. That is why call sites across the
   * app each hand-roll `!bg-blue-700 hover:!bg-blue-800 …`, duplicating the
   * brand colour instead of taking it from the theme.
   *
   * The default is now the `primary` token from tailwind.config (#1d4ed8),
   * written as a literal class so it is actually emitted.
   *
   * NOTE: the `color` path is left exactly as it was. Call sites passing a
   * palette NAME ("gray", "blue") build real classes and still work, so this
   * change is additive — nothing that renders today changes, except buttons
   * that had no background at all.
   */
  const colorClasses = color
    ? `bg-${color}-600 hover:bg-${color}-700 focus:ring-${color}-300 dark:bg-${color}-600 dark:hover:bg-${color}-700 dark:focus:ring-${color}-800`
    : "bg-primary hover:bg-blue-800 focus:ring-blue-300 dark:hover:bg-blue-800 dark:focus:ring-blue-800";
  
  return (
    <>
      <button
        type={type}
        onClick={onClick}
        disabled={isButtonDisabled}
        className={`${fullWidth ? "w-full" : ""} text-white ${colorClasses} focus:ring-1 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center ${
          isButtonDisabled ? "cursor-not-allowed opacity-50" : ""
        } ${className ?? ""} flex items-center justify-center gap-2`}
        {...rest}
      >
        {loading && (
          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {children}
      </button>
    </>
  );
}
